import { chartScale, EPS, monthName, RULES, type MonthRow } from "../lib/biltCash";
import { formatExact } from "../lib/money";

const W = 720;
const H = 280;
const LEFT = 44;
const RIGHT = 12;
const TOP = 18;
const BOTTOM = 34;

/**
 * Bilt Cash on each rent day at the least spend that covers it, amber dots
 * where rent is not fully unlocked. Plain SVG, drawn from one tested scale (`chartScale`), because
 * a recharts ResponsiveContainer needs a ResizeObserver jsdom does not have.
 */
export function BiltCashChart({ planned }: { planned: MonthRow[] }) {
  const unlockCost = planned[0]?.unlockCost ?? 0;
  const { max, ticks } = chartScale([
    unlockCost,
    ...planned.map((r) => r.cashOnRentDay),
  ]);
  const slot = (W - LEFT - RIGHT) / Math.max(1, planned.length);
  const x = (i: number) => LEFT + slot * i + slot / 2;
  const y = (v: number) => TOP + (H - TOP - BOTTOM) * (1 - v / max);
  const grantIndex = planned.findIndex((r) => r.granted > 0);
  const grant = planned[grantIndex]?.granted ?? 0;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Bilt Cash on each rent day"
      className="block h-auto w-full min-w-[36rem] tabular-nums"
    >
      {ticks.map((v) => (
        <g key={v}>
          <line x1={LEFT} x2={W - RIGHT} y1={y(v)} y2={y(v)} stroke="#262c36" />
          <text x={LEFT - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="#7a808c">
            ${v}
          </text>
        </g>
      ))}

      {grantIndex >= 0 && (
        <g>
          <line
            x1={LEFT + slot * grantIndex}
            x2={LEFT + slot * grantIndex}
            y1={TOP - 6}
            y2={H - BOTTOM}
            stroke="#353c48"
            strokeDasharray="2 3"
          />
          <text x={LEFT + slot * grantIndex + 5} y={TOP + 2} fontSize={11} fill="#9a9fab">
            Anniversary · +${grant}
          </text>
        </g>
      )}

      <line
        x1={LEFT}
        x2={W - RIGHT}
        y1={y(unlockCost)}
        y2={y(unlockCost)}
        stroke="#e2a64e"
        strokeWidth={1.5}
        strokeDasharray="5 4"
      />
      <text x={LEFT + 4} y={y(unlockCost) - 5} fontSize={11} fill="#e2a64e">
        Rent unlock {formatExact(unlockCost)}
      </text>

      <polyline
        points={planned.map((r, i) => `${x(i)},${y(r.cashOnRentDay)}`).join(" ")}
        fill="none"
        stroke="#86a8ff"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {planned.map((r, i) => (
        <g key={`${r.year}-${r.month}`}>
          <circle
            data-series="planned"
            cx={x(i)}
            cy={y(r.cashOnRentDay)}
            r={4}
            fill={r.short > EPS ? "#e2a64e" : "#86a8ff"}
            stroke="#151920"
            strokeWidth={1.5}
          />
          {r.bonuses > 0 && (
            <text x={x(i)} y={y(r.cashOnRentDay) - 9} textAnchor="middle" fontSize={10} fontWeight={600} fill="#6fbf8b">
              +${r.bonuses * RULES.milestoneCash}
            </text>
          )}
          <text x={x(i)} y={H - BOTTOM + 16} textAnchor="middle" fontSize={11} fill="#9a9fab">
            {monthName(r.month)}
          </text>
          {(i === 0 || r.month === 0) && (
            <text x={x(i)} y={H - BOTTOM + 29} textAnchor="middle" fontSize={10} fill="#7a808c">
              {r.year}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
